import { PERMISSION_DENIED_MESSAGE } from '@models/permissions';
import { formatFieldViolations } from '@utils/grpc-field-violations.utils';

const GRPC_STATUS_PREFIX_PATTERN = /^\d+\s+[A-Z_]+:\s*/;

/** gRPC status codes */
export const GrpcStatusCode = {
  INVALID_ARGUMENT: 3,
  DEADLINE_EXCEEDED: 4,
  NOT_FOUND: 5,
  PERMISSION_DENIED: 7,
  UNAVAILABLE: 14,
  UNAUTHENTICATED: 16,
} as const;

/**
 * Erro sintético para request abortada no cliente por falta de credencial.
 *
 * Reproduz o formato de um erro gRPC (`code` + `message`) para que guards e
 * telas classifiquem o caso como sessão expirada. Sem isso a request sairia sem
 * `Authorization`, o backend não resolveria o usuário e devolveria NOT_FOUND —
 * mascarando sessão expirada como "usuário não encontrado".
 */
export class UnauthenticatedError extends Error {
  readonly code = GrpcStatusCode.UNAUTHENTICATED;

  constructor(message = 'Sessão expirada: nenhuma credencial válida disponível.') {
    super(message);
    this.name = 'UnauthenticatedError';
  }
}

function normalizeBackendMessage(message: string): string {
  const trimmed = message.trim();
  if (!trimmed) {
    return '';
  }

  const withoutGrpcPrefix = trimmed.replace(GRPC_STATUS_PREFIX_PATTERN, '').trim();
  return withoutGrpcPrefix || trimmed;
}

export function extractMessageFromUnknown(error: unknown): string {
  if (typeof error === 'string') {
    return normalizeBackendMessage(error);
  }

  if (error instanceof Error) {
    return normalizeBackendMessage(error.message);
  }

  if (error && typeof error === 'object' && 'message' in error) {
    const message = (error as { message?: unknown }).message;

    if (typeof message === 'string') {
      return normalizeBackendMessage(message);
    }
  }

  return '';
}

export function extractStatusCode(error: unknown): number | null {
  if (!error || typeof error !== 'object') {
    return null;
  }

  const candidate = error as { code?: unknown; status?: unknown; statusCode?: unknown };

  if (typeof candidate.code === 'number') {
    return candidate.code;
  }
  if (typeof candidate.status === 'number') {
    return candidate.status;
  }
  if (typeof candidate.statusCode === 'number') {
    return candidate.statusCode;
  }

  return null;
}

/**
 * Detects permission/authorization failures from gRPC or HTTP-style errors.
 * - gRPC 7 PERMISSION_DENIED
 * - HTTP 401 / 403 (when exposed on the error object)
 */
export function isPermissionDeniedError(error: unknown): boolean {
  const code = extractStatusCode(error);

  if (
    code === GrpcStatusCode.PERMISSION_DENIED ||
    code === 401 ||
    code === 403
  ) {
    return true;
  }

  const message = extractMessageFromUnknown(error).toLowerCase();
  if (!message) {
    return false;
  }

  return (
    message.includes('permission_denied') ||
    message.includes('permission denied') ||
    message.includes('not authorized') ||
    message.includes('não autorizado') ||
    message.includes('nao autorizado') ||
    message.includes('forbidden') ||
    message.includes('access denied')
  );
}

/**
 * Returns the backend error message when available; otherwise uses the fallback.
 *
 * Quando o servidor manda violações de campo estruturadas
 * (`google.rpc.BadRequest` no trailer), elas têm preferência: dizem qual campo
 * quebrou qual regra, e vêm todas de uma vez.
 */
export function getBackendErrorMessage(error: unknown, fallback: string): string {
  if (isPermissionDeniedError(error)) {
    const message = extractMessageFromUnknown(error);
    return message || PERMISSION_DENIED_MESSAGE;
  }

  const violations = formatFieldViolations(error);
  if (violations) {
    return violations;
  }

  const message = extractMessageFromUnknown(error);
  return message || fallback;
}

export function getPermissionDeniedMessage(error?: unknown): string {
  if (!error) {
    return PERMISSION_DENIED_MESSAGE;
  }
  const message = extractMessageFromUnknown(error);
  return message || PERMISSION_DENIED_MESSAGE;
}
